import React, { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { AuthorsUpdateModal } from './AuthorsUpdateModal';
import { FieldHeader } from 'src/components/Submission/GenericFieldComponents/common/FieldHeader';
import { TextEmptyState } from '../Text/TextEmptyState';
import {
  IAuthorWithContribution,
  IAvailableWorkflowActions,
  IRoleType
} from '@publishing-engine/domain/models';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { EntryPoint } from 'src/utils/types';
import { AuthorsDisplay } from './AuthorsDisplay';
import { AuthorsDiffModal } from './AuthorsDiffModal';
import AuthorSubmissionFormDataProvider from 'src/contexts/SubmissionContextProvider';
import { ensureAllUsers } from '../../../../api/queries';
import _ from 'lodash';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';

type IProps = {
  formFieldDefinition: FormFieldAuthors;
  value: IAuthorWithContribution[];
  previousValue?: IAuthorWithContribution[];
  availableActions: IAvailableWorkflowActions;
  onChange: (value: IAuthorWithContribution[]) => void;
  entryPoint: EntryPoint;
  authorRoleMap?: IAuthorRolesMap;
  isUpdateRequired?: boolean;
  isRevision?: boolean;
  roleContext?: IRoleType;
};

export const AuthorsField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  authorRoleMap,
  isUpdateRequired,
  isRevision,
  roleContext
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isEditable = !!onChange;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const onSubmit = async (authors: IAuthorWithContribution[]) => {
    if (_.isEqual(value, authors)) return setIsEditing(false);

    setIsSubmitting(true);

    const coAuthorsEmails = authors.map(author => author.email);
    const usersResponse = await ensureAllUsers(coAuthorsEmails);
    const responseMap = new Map(
      usersResponse.map(user => [user.email, user.userId])
    );

    const updatedValue = authors.map(author => ({
      ...author,
      email: author.email.toLowerCase(),
      id: responseMap.get(author.email.toLowerCase())
    }));

    onChange(updatedValue);
    setIsSubmitting(false);
    setIsEditing(false);
  };

  return (
    <AuthorSubmissionFormDataProvider>
      <Flex
        key={value.map(author => author.email).join('-')}
        flexDirection={'column'}
        gap={2}
        id='All Authors'
      >
        {isEditing && authorRoleMap && (
          <AuthorsUpdateModal
            formField={formFieldDefinition}
            onClose={() => {
              setIsEditing(false);
            }}
            value={value}
            onSubmit={onSubmit}
            authorRoleMap={authorRoleMap}
            isSubmitting={isSubmitting}
            isRevision={isRevision}
          />
        )}

        {isUpdated && showDiffModal ? (
          <AuthorsDiffModal
            config={formFieldDefinition}
            value={value}
            previousValue={previousValue}
            onClose={closeDiffModal}
          />
        ) : null}

        <Box>
          <FieldHeader
            {...formFieldDefinition}
            label={`All Authors (${value.length ?? 0})`}
            isUpdated={isUpdated}
            editCallback={editCallback}
            onShowDiffModal={openDiffModal}
            availableActions={availableActions}
            entryPoint={entryPoint}
            sectionHeadingTextStyle="headingGroup"
            isUpdateRequired={isUpdateRequired}
          />
        </Box>

        {value ? (
          <AuthorsDisplay value={value} config={formFieldDefinition} roleContext={roleContext} />
        ) : (
          <TextEmptyState />
        )}
      </Flex>
    </AuthorSubmissionFormDataProvider>
  );
};
